import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db, ensureSessionPersistence, getAuthClient } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { resolveOnTimeSessionForUser, writeOnTimeSession } from '../auth/session';
import { formatOnTimeRoleLabel, normalizeOnTimeRole, hasOnTimeAccess } from '../utils/access';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { storeStartPacking, storeSuggestSwap, storeReadyForDriver, storePackedWithProof, storeMarkCollected, storeCompleteOtherDriver, storeRequestCancelApproval, storeWithdrawCancelApproval, storeMarkItemUnavailable, storeReactivateItem, storeMoveBackToWaiting, storeAddSwapProduct, storeRemoveSwapProduct, siteSelectDriver, siteClearSelectedDriver, linkOrderSendMessage } from '../lib/actions';
import { getActiveStoreNames, loadOnTimeSettings } from '../lib/ontimeSettings';
import { isClosedOperationalOrder, isDemoOrGhostOrder } from '../lib/liveOrderFilters';
import '../styles/linkPwa.css';

const STORE_App_SESSION_KEY = 'matrixos.link.storePwa.session.v1';
const LINK_SITE_BASE_PATHS = ['/site', '/link-store', '/link/site'];

const LINK_CHAT_EMOJIS = ['👍','🙏','✅','🚚','📦','⏱️','⚠️','🔥','💬','🙌','😊','👌'];
const LINK_CHAT_STICKERS = [
  { type: 'sticker', label: 'Packed', value: '📦 Packed' },
  { type: 'sticker', label: 'On it', value: '✅ On it' },
  { type: 'sticker', label: 'Need help', value: '⚠️ Need help' },
  { type: 'sticker', label: 'Driver waiting', value: '🚚 Driver waiting' },
];
const LINK_CHAT_GIFS = [
  { type: 'gif', label: 'Confirmed', value: '✅ Confirmed' },
  { type: 'gif', label: 'Packing now', value: '📦 Packing now' },
  { type: 'gif', label: 'Almost ready', value: '⏱️ Almost ready' },
  { type: 'gif', label: 'Thank you', value: '🙏 Thank you' },
];


function siteBasePath() {
  if (typeof window === 'undefined') return '/site';
  const path = window.location.pathname || '/site';
  return LINK_SITE_BASE_PATHS.find((base) => path === base || path.startsWith(`${base}/`)) || '/site';
}

function readSiteRoute() {
  if (typeof window === 'undefined') return { mode: 'home' };
  const base = siteBasePath();
  const rest = (window.location.pathname || '').slice(base.length).replace(/^\/+/, '');
  const parts = rest.split('/').filter(Boolean).map(decodeURIComponent);
  if (parts[0] === 'login') return { mode: 'login' };
  if (parts[0] === 'access' || parts[0] === 'unlock') return { mode: 'access' };
  if (parts[0] === 'order' && parts[1]) return { mode: 'order', orderId: parts[1], step: parts[2] || '' };
  if (parts[0] === 'chat' && parts[1]) return { mode: 'chat', orderId: parts[1] };
  if (parts[0] === 'lane' && parts[1]) return { mode: 'lane', step: parts[1] };
  return { mode: 'home' };
}

function pushSiteAuthPath(mode = 'home', replace = true) {
  if (typeof window === 'undefined') return;
  const base = siteBasePath();
  const path = mode === 'login' ? `${base}/login` : mode === 'access' ? `${base}/access` : base;
  if (window.location.pathname !== path) {
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({ linkSiteRoute: { mode } }, '', path);
  }
}

function pushSiteRoute(route = {}) {
  if (typeof window === 'undefined') return;
  const base = siteBasePath();
  let path = base;
  if (route.mode === 'order' && route.orderId) path = `${base}/order/${encodeURIComponent(route.orderId)}${route.step ? `/${encodeURIComponent(route.step)}` : ''}`;
  if (route.mode === 'chat' && route.orderId) path = `${base}/chat/${encodeURIComponent(route.orderId)}`;
  if (route.mode === 'lane' && route.step) path = `${base}/lane/${encodeURIComponent(route.step)}`;
  if (window.location.pathname !== path) window.history.pushState({ linkSiteRoute: route }, '', path);
}

function cleanPin(value = '') {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

function profileName(profile = {}) {
  return String(profile.displayName || `${profile.fullName || ''} ${profile.surname || ''}`.trim() || profile.name || profile.email || 'Site profile').trim();
}

function profilePin(profile = {}) {
  return cleanPin(profile.accessPin || profile.pin || profile.linkPin || profile.onTimePin || '');
}

function isActive(profile = {}) {
  return profile.active !== false && profile.disabled !== true && profile.archived !== true && profile.removed !== true;
}

function readStoreSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORE_App_SESSION_KEY) || window.sessionStorage.getItem(STORE_App_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function writeStoreSession(session) {
  if (typeof window === 'undefined') return;
  try {
    const raw = JSON.stringify({ ...session, savedAt: Date.now() });
    window.localStorage.setItem(STORE_App_SESSION_KEY, raw);
    window.sessionStorage.setItem(STORE_App_SESSION_KEY, raw);
  } catch (_) {}
}

function clearStoreSession() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORE_App_SESSION_KEY);
    window.sessionStorage.removeItem(STORE_App_SESSION_KEY);
  } catch (_) {}
}

function getTime(row) {
  const t = row?.timings || {};
  const candidates = [
    t.sentToStoreAt, t.storeReceivedAt, t.orderReceivedAt, t.createdAt,
    row?.sentToStoreAt, row?.placedAt, row?.createdAt, row?.orderCreatedAt, row?.receivedAt, row?.raw?.createdAt,
  ];
  const times = candidates.map(safeMs).filter(Boolean);
  const raw = times.length ? Math.min(...times) : safeMs(row?.updatedAt || row?.raw?.updatedAt);
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function minutesSince(row, now = Date.now()) {
  return Math.max(0, Math.floor((now - getTime(row).getTime()) / 60000));
}

function fmtMins(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function money(n) {
  return `R ${Number(n || 0).toFixed(0)}`;
}


function chatMessages(order = {}) {
  const direct = Array.isArray(order.linkChatMessages) ? order.linkChatMessages : [];
  const raw = Array.isArray(order.raw?.linkChatMessages) ? order.raw.linkChatMessages : [];
  return [...raw, ...direct]
    .filter(Boolean)
    .filter((msg, idx, arr) => arr.findIndex((m) => String(m.id || m.createdAt || m.text) === String(msg.id || msg.createdAt || msg.text)) === idx)
    .sort((a, b) => safeMs(a.createdAt) - safeMs(b.createdAt));
}

function chatReadKey(orgId, orderId, role = 'store') {
  return `matrixos.link.chat.read.${orgId}.${orderId}.${role}`;
}

function readChatSeenAt(orgId, orderId, role = 'store') {
  if (typeof window === 'undefined') return 0;
  try { return Number(window.localStorage.getItem(chatReadKey(orgId, orderId, role)) || 0) || 0; } catch (_) { return 0; }
}

function writeChatSeenAt(orgId, orderId, role = 'store') {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(chatReadKey(orgId, orderId, role), String(Date.now())); } catch (_) {}
}

function unreadChatCount(order = {}, orgId = '', role = 'store') {
  const seenAt = readChatSeenAt(orgId, order.id, role);
  return chatMessages(order).filter((msg) => safeMs(msg.createdAt) > seenAt && String(msg.actorRole || '').toLowerCase() !== role).length;
}

function chatTimeLabel(raw) {
  const ms = safeMs(raw);
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function productPrice(product = {}, qty = 1) {
  const safeQty = Math.max(0.5, Number(qty) || 1);
  const directUnit = Number(
    product.price ??
    product.sellPrice ??
    product.unitPrice ??
    product.amount ??
    product.sellingPrice ??
    product.retailPrice ??
    product.priceEach ??
    product.pricing?.price ??
    product.pricing?.unitPrice ??
    0
  ) || 0;
  if (directUnit > 0) return directUnit * safeQty;
  if (Number(product.lineTotal) > 0) return Number(product.lineTotal);
  const rules = Array.isArray(product.priceRules) ? product.priceRules : Array.isArray(product.pricing?.priceRules) ? product.pricing.priceRules : [];
  const exact = rules.find((rule) => Math.abs((Number(rule?.qty ?? rule?.quantity) || 0) - safeQty) < 0.0001);
  if (exact && Number(exact.price ?? exact.amount) > 0) return Number(exact.price ?? exact.amount);
  const baseQty = Math.max(0.0001, Number(product.baseQty ?? product.qty ?? product.quantity ?? product.pricing?.baseQty ?? 1) || 1);
  const basePrice = Number(product.basePrice ?? product.baseAmount ?? product.pricing?.basePrice ?? product.pricing?.baseAmount ?? 0) || 0;
  return basePrice > 0 ? (basePrice / baseQty) * safeQty : 0;
}

function isCardPayment(order = {}) {
  const p = paymentMethod(order).toLowerCase();
  return p.includes('card') || p.includes('paid') || p.includes('online');
}

function isSwapItem(item = {}) {
  const id = String(item.id || item.lineId || item.productId || '').toLowerCase();
  const status = String(item.status || item.itemStatus || item.type || '').toLowerCase();
  return item.isSwap === true || item.isAddition === true || item.addedBySite || item.addedAtLabel || item.swapFor || id.startsWith('swap_') || status.includes('addition') || status.includes('swap');
}

function originalOrderItems(order = {}) {
  const snap = Array.isArray(order.originalOrderItems) ? order.originalOrderItems : Array.isArray(order.raw?.originalOrderItems) ? order.raw.originalOrderItems : [];
  if (snap.length) return snap;
  return orderItems(order).filter((item) => !isSwapItem(item));
}

function swapOrderItems(order = {}) {
  const fromItems = orderItems(order).filter(isSwapItem);
  const fromAdditions = Array.isArray(order.additions) ? order.additions : Array.isArray(order.raw?.additions) ? order.raw.additions : [];
  const seen = new Set();
  return [...fromItems, ...fromAdditions].filter((item, idx) => {
    const key = String(item.id || item.lineId || item.productId || itemName(item) || idx);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function calcOriginalSubtotal(order = {}) {
  return originalOrderItems(order).reduce((sum, item) => sum + itemPrice(item), 0);
}

function calcSwapSubtotal(order = {}) {
  return swapOrderItems(order).reduce((sum, item) => sum + itemPrice(item), 0);
}

function productLooksUnavailable(product = {}) {
  return product.active === false || product.archived === true || product.disabled === true || product.unavailable === true || product.available === false;
}

function productMatchesUnavailable(product = {}, unavailableSet = new Set()) {
  const candidates = [product.id, product.productId, product.sku, product.name, product.productName, product.title]
    .filter(Boolean)
    .map(String);
  return candidates.some((v) => unavailableSet.has(v));
}

function safeMs(raw) {
  if (!raw) return 0;
  if (raw?.toDate) return raw.toDate().getTime();
  if (raw instanceof Date) return raw.getTime();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function elapsedMinutesBetween(start, end, fallbackEnd = Date.now()) {
  const a = safeMs(start);
  if (!a) return null;
  const b = safeMs(end) || fallbackEnd;
  return Math.max(0, Math.floor((b - a) / 60000));
}

function orderStepDurations(order = {}, now = Date.now()) {
  const t = order?.timings || {};
  const step = siteStep(order);
  const sent = t.sentToStoreAt || t.storeReceivedAt || t.orderReceivedAt || order?.sentToStoreAt || order?.placedAt || order?.createdAt || getTime(order);
  const packingStart = t.packingStartedAt;
  const readyStart = t.readyForDriverAt || t.packedAt;
  const collectedStart = t.collectedAt || t.driverPickedUpAt || t.outForDeliveryAt;
  const deliveredAt = t.deliveredAt || t.completedAt || t.driverDeliveredAt || order?.deliveredAt || order?.completedAt;

  const waitingEnd = packingStart || readyStart || collectedStart || deliveredAt || (step === 'waiting' ? now : null);
  const packingEnd = readyStart || collectedStart || deliveredAt || (step === 'packing' ? now : null);
  const readyEnd = collectedStart || deliveredAt || (step === 'ready' ? now : null);
  const collectedEnd = deliveredAt || (step === 'collected' ? now : null);
  const totalEnd = deliveredAt || (['waiting', 'packing', 'ready', 'collected'].includes(step) ? now : null);

  const waiting = elapsedMinutesBetween(sent, waitingEnd, now);
  const packing = packingStart ? elapsedMinutesBetween(packingStart, packingEnd, now) : null;
  const ready = readyStart ? elapsedMinutesBetween(readyStart, readyEnd, now) : null;
  const collected = collectedStart ? elapsedMinutesBetween(collectedStart, collectedEnd, now) : null;
  const total = elapsedMinutesBetween(sent, totalEnd, now);

  return {
    waiting: waiting ?? (step === 'waiting' ? minutesSince(order, now) : null),
    packing: packing ?? (step === 'packing' ? minutesSinceStep(order, now) : null),
    ready: ready ?? (step === 'ready' ? minutesSinceStep(order, now) : null),
    collected: collected ?? (step === 'collected' ? minutesSinceStep(order, now) : null),
    total: total ?? minutesSince(order, now),
  };
}


function needsSwapApproval(order = {}) {
  return unavailableNames(order).size > 0 || swapOrderItems(order).length > 0;
}

function approvalWaitMinutes(order = {}, now = Date.now()) {
  const raw = order?.timings?.swapApprovalStartedAt || order?.timings?.unavailableMarkedAt || order?.timings?.sentToStoreAt || order?.sentToStoreAt || order?.createdAt;
  const mins = elapsedMinutesBetween(raw, null, now);
  return mins == null ? 0 : mins;
}

function orderItems(order = {}) {
  const rows = Array.isArray(order.items) ? order.items : [];
  const rawRows = Array.isArray(order.raw?.items) ? order.raw.items : [];
  if (!rawRows.length) return rows;
  return rows.map((item, index) => ({ ...(rawRows[index] || {}), ...item }));
}

function unavailableNames(order = {}) {
  const raw = [
    ...(Array.isArray(order.unavailableItems) ? order.unavailableItems : []),
    ...(Array.isArray(order.flags?.unavailableItems) ? order.flags.unavailableItems : []),
  ];
  orderItems(order).forEach((item, idx) => {
    if (item?.unavailable === true || item?.unavailableBySite === true) {
      raw.push(itemKey(item, idx));
    }
  });
  originalOrderItems(order).forEach((item, idx) => {
    if (item?.unavailable === true || item?.unavailableBySite === true) {
      raw.push(itemKey(item, idx));
    }
  });
  if (order.flags?.unavailableMap && typeof order.flags.unavailableMap === 'object') {
    Object.entries(order.flags.unavailableMap).forEach(([key, value]) => { if (value) raw.push(key); });
  }
  return new Set(raw.filter(Boolean).map(String));
}

function itemName(item = {}) {
  return String(item.name || item.productName || item.title || item.sku || 'Item').trim();
}

function itemQty(item = {}) {
  return Number(item.qty ?? item.quantity ?? item.count ?? 1) || 1;
}

function itemPrice(item = {}) {
  const direct = Number(item.lineTotal ?? item.total ?? item.amount ?? 0);
  if (direct) return direct;
  const unit = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.value ?? 0) || 0;
  return unit * itemQty(item);
}

function itemKey(item = {}, idx = 0) {
  return String(
    item.__lineKey ||
    item.lineKey ||
    item.cartLineId ||
    item.lineId ||
    item.orderLineId ||
    item.rowId ||
    item.uid ||
    `line_${idx}_${itemName(item)}_${itemQty(item)}_${itemPrice(item)}`
  );
}

function activeOrderItems(order = {}) {
  const unavailable = unavailableNames(order);
  return orderItems(order).filter((item, idx) => !unavailable.has(itemKey(item, idx)));
}

function calcActiveSubtotal(order = {}) {
  return activeOrderItems(order).reduce((sum, item) => sum + itemPrice(item), 0);
}

function deliveryFee(order = {}) {
  return Number(order.deliveryFee ?? order.delivery ?? order.fee ?? order.deliveryAmount ?? 0) || 0;
}

function originalPaidTotal(order = {}, fallbackSubtotal = 0, delivery = 0) {
  const raw = order.raw || {};
  const candidates = [
    raw.originalPaidTotal,
    raw.originalOrderTotal,
    raw.pricing?.originalTotal,
    raw.pricing?.paidTotal,
    raw.pricing?.total,
    raw.total,
    order.originalPaidTotal,
    order.originalOrderTotal,
    order.total,
  ];
  const resolved = candidates.map(Number).find((v) => Number.isFinite(v) && v > 0);
  return resolved != null ? resolved : fallbackSubtotal + delivery;
}

function paymentMethod(order = {}) {
  return String(order.paymentMethod || order.payment || order.paymentType || order.payment_status || order.paymentStatus || 'Not set').replace(/_/g, ' ');
}

function customerDisplay(order = {}) {
  return String(order.customerName || `${order.customer?.firstName || ''} ${order.customer?.surname || ''}`.trim() || 'Customer').trim();
}

function cleanSenderName(value = '') {
  const text = typeof value === 'string' ? value : '';
  const clean = text.replace(/_/g, ' ').trim();
  return /^(online\s*team|online|team)$/i.test(clean) ? '' : clean;
}

function onlineSender(order = {}) {
  const raw = order.raw || {};
  return cleanSenderName(
    order.sentByName || raw.sentByName || raw.senderName || raw.createdByName || raw.createdByDisplayName ||
    raw.createdBy?.displayName || raw.createdBy?.fullName || raw.createdBy?.name ||
    order.senderName || order.createdByName || order.createdByDisplayName ||
    order.createdBy?.displayName || order.createdBy?.fullName || order.createdBy?.name ||
    order.createdByProfile?.displayName || order.createdByProfile?.fullName || order.createdByProfile?.name ||
    order.createdByUser?.displayName || order.createdByUser?.fullName || order.createdByUser?.name ||
    order.onlineProfile?.displayName || order.onlineProfile?.fullName || order.onlineProfile?.name ||
    order.profileName || order.userName || order.onlineUserName || order.onlineName || order.handledByName || order.handledBy || order.ownerName
  ) || 'Online Team';
}

function roleLabelFromValue(value = '') {
  const v = String(value || '').replace(/_/g, ' ').replace(/-/g, ' ').trim().toLowerCase();
  if (!v) return '';
  if (v.includes('owner')) return 'Owner';
  if (v.includes('admin')) return 'Admin';
  if (v.includes('driver')) return 'Driver';
  if (v.includes('store') || v.includes('site')) return 'Store';
  if (v.includes('online')) return 'Online Team';
  return v.replace(/\b\w/g, (m) => m.toUpperCase());
}

function onlineTeamLabel(order = {}) {
  const raw = order.raw || {};
  const explicit = order.senderRoleLabel || order.sentByRoleLabel || order.createdByRoleLabel ||
    raw.senderRoleLabel || raw.sentByRoleLabel || raw.createdByRoleLabel ||
    order.createdBy?.roleLabel || raw.createdBy?.roleLabel || order.createdByProfile?.roleLabel || raw.createdByProfile?.roleLabel ||
    order.createdByUser?.roleLabel || raw.createdByUser?.roleLabel;
  const role = explicit || order.senderRole || order.sentByRole || order.createdByRole || raw.senderRole || raw.sentByRole || raw.createdByRole || order.createdBy?.role || raw.createdBy?.role;
  return roleLabelFromValue(role) || 'Admin';
}


function pendingDriverRequests(order = {}) {
  const rows = order.driverRequests || order.raw?.driverRequests || [];
  return Array.isArray(rows) ? rows.filter((row) => row?.driverId || row?.driverName) : [];
}

function driverDisplay(order = {}) {
  return String(order.assignedDriverName || order.assignedDriver || order.driverName || order.driver?.name || order.assignment?.driverName || order.deliveredBy || order.deliveredByName || '').trim();
}

function hasSelectedLinkDriver(order = {}) {
  return Boolean(order?.assignment?.siteSelectedDriver || order?.assignment?.driverId || order?.assignedDriverName || order?.assignedDriver);
}

function deliveryMode(order = {}, override = '') {
  const raw = String(override || order?.deliveryMode || order?.handoffMode || order?.assignment?.deliveryMode || order?.assignment?.handoffMode || '').toLowerCase();
  return raw.includes('other') || raw.includes('uber') ? 'other' : 'link';
}

function storeStageLabel(step, order = {}, issue = false) {
  const driverName = driverDisplay(order);
  if (step === 'completed') return `Completed${driverName ? ` · ${driverName}` : ''}`;
  if (step === 'collected') return `Collected${driverName ? ` · ${driverName}` : ''}`;
  if (step === 'ready') return 'Ready for collection';
  if (step === 'packing') return 'Packing now';
  return issue ? 'Issue review' : 'Site action';
}


function storeName(order) {
  return order?.store || order?.storeName || order?.assignedStore || order?.assignment?.storeName || 'Unassigned Store';
}

function waitingForStore(order) {
  return ['awaiting_store', 'issue', 'store_packing', 'packing'].includes(String(order?.stage || '').toLowerCase());
}

function siteStep(order = {}) {
  const stage = String(order?.stage || '').toLowerCase();
  const storeStatus = String(order?.storeStatus || order?.fulfilment?.storeStatus || '').toLowerCase();
  if (storeStatus === 'collected' || order?.timings?.collectedAt) return 'collected';
  if (stage === 'ready_for_driver' || storeStatus === 'ready_for_driver' || storeStatus === 'ready' || storeStatus === 'packed' || order?.proof?.packedPhotoUrl || order?.storeProof?.packedPhotoUrl) return 'ready';
  if (['packing', 'store_packing'].includes(storeStatus) || ['packing', 'store_packing'].includes(stage)) return 'packing';
  if (stage === 'issue' || order?.issueFlag || order?.flags?.hasStockIssue) return 'waiting';
  if (['awaiting_store', 'new', 'sent_to_store'].includes(stage) || !stage) return 'waiting';
  return 'waiting';
}

function isArchivedSiteOrder(order = {}) {
  const raw = order?.raw || {};
  const statusFields = [
    order?.stage,
    order?.status,
    order?.subStatus,
    order?.storeStatus,
    order?.driverStatus,
    order?.fulfilment?.storeStatus,
    order?.fulfilment?.driverStatus,
    raw.stage,
    raw.status,
    raw.subStatus,
    raw.storeStatus,
    raw.driverStatus,
    raw.fulfilment?.storeStatus,
    raw.fulfilment?.driverStatus,
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean);

  return Boolean(
    isClosedOperationalOrder(order) ||
    order?.archived || raw.archived || order?.archivedAt || raw.archivedAt ||
    order?.cancelled || order?.canceled || raw.cancelled || raw.canceled ||
    order?.archiveCategory || raw.archiveCategory || order?.reroutedToOrderId || raw.reroutedToOrderId ||
    statusFields.some((value) => ['archived', 'archive', 'cancelled', 'canceled', 'delivered', 'complete', 'completed'].includes(value))
  );
}

function isSitePwaOrder(order = {}) {
  const stage = String(order?.stage || '').toLowerCase();
  if (isArchivedSiteOrder(order)) return false;
  if (['delivered', 'completed'].includes(stage)) return true;
  return ['waiting', 'packing', 'ready', 'collected', 'completed'].includes(siteStep(order));
}

function stepTimerStart(order = {}) {
  const step = siteStep(order);
  const t = order?.timings || {};
  if (step === 'collected') return t.collectedAt || t.readyForDriverAt || t.packedAt || t.packingStartedAt || t.sentToStoreAt;
  if (step === 'ready') return t.readyForDriverAt || t.packedAt || t.packingStartedAt || t.sentToStoreAt;
  if (step === 'proof') return t.packedAt || t.packingStartedAt || t.sentToStoreAt;
  if (step === 'packing') return t.packingStartedAt || t.sentToStoreAt;
  return t.sentToStoreAt || order?.sentToStoreAt || order?.updatedAt || order?.placedAt || order?.createdAt;
}

function minutesSinceStep(order, now = Date.now()) {
  const raw = stepTimerStart(order);
  const d = raw?.toDate ? raw.toDate() : raw instanceof Date ? raw : raw ? new Date(raw) : getTime(order);
  const safe = Number.isNaN(d.getTime()) ? getTime(order) : d;
  return Math.max(0, Math.floor((now - safe.getTime()) / 60000));
}

function avgMins(rows = [], now = Date.now(), mode = 'order') {
  if (!rows.length) return 0;
  const total = rows.reduce((sum, row) => sum + (mode === 'step' ? minutesSinceStep(row, now) : minutesSince(row, now)), 0);
  return Math.round(total / rows.length);
}

function avgStepDuration(rows = [], key, now = Date.now()) {
  const values = rows
    .map((row) => orderStepDurations(row, now)[key])
    .filter((value) => value != null && Number.isFinite(Number(value)));
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + Number(value), 0) / values.length);
}

function avgActiveStoreDuration(rows = [], now = Date.now()) {
  const values = [];
  rows.forEach((row) => {
    const step = siteStep(row);
    if (!['waiting', 'packing', 'ready'].includes(step)) return;
    const d = orderStepDurations(row, now);
    const value = step === 'waiting' ? d.waiting : step === 'packing' ? d.packing : d.ready;
    if (value != null && Number.isFinite(Number(value))) values.push(Number(value));
  });
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function avgStoreWorkDuration(rows = [], now = Date.now()) {
  const values = [];
  rows.forEach((row) => {
    const d = orderStepDurations(row, now);
    const parts = [d.waiting, d.packing, d.ready].filter((value) => value != null && Number.isFinite(Number(value)));
    if (parts.length) values.push(parts.reduce((sum, value) => sum + Number(value), 0));
  });
  if (!values.length) return 0;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date = new Date()) {
  const d = startOfDay(date);
  d.setDate(d.getDate() + 1);
  d.setMilliseconds(-1);
  return d;
}

function startOfWeek(date = new Date()) {
  const d = startOfDay(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d;
}

function periodRange(period = 'today', customDate = '') {
  const now = new Date();
  if (period === 'yesterday') {
    const d = startOfDay(now);
    d.setDate(d.getDate() - 1);
    return { start: d.getTime(), end: endOfDay(d).getTime(), label: 'Yesterday' };
  }
  if (period === 'thisWeek') {
    return { start: startOfWeek(now).getTime(), end: now.getTime(), label: 'Current week' };
  }
  if (period === 'lastWeek') {
    const start = startOfWeek(now);
    start.setDate(start.getDate() - 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    end.setMilliseconds(-1);
    return { start: start.getTime(), end: end.getTime(), label: 'Last week' };
  }
  if (period === 'monthToDate') {
    const start = startOfDay(now);
    start.setDate(1);
    return { start: start.getTime(), end: now.getTime(), label: 'Month to date' };
  }
  if (period === 'specific') {
    const chosen = customDate ? new Date(`${customDate}T00:00:00`) : now;
    return { start: startOfDay(chosen).getTime(), end: endOfDay(chosen).getTime(), label: customDate || 'Specific date' };
  }
  return { start: startOfDay(now).getTime(), end: now.getTime(), label: 'Today' };
}

function isInRange(raw, range) {
  const t = safeMs(raw);
  return Boolean(t && t >= range.start && t <= range.end);
}

function orderCreatedTime(order = {}) {
  return order?.timings?.sentToStoreAt || order?.sentToStoreAt || order?.createdAt || order?.placedAt || order?.updatedAt;
}

function orderDeliveredTime(order = {}) {
  const t = order?.timings || {};
  return t.deliveredAt || t.completedAt || t.driverDeliveredAt || order?.deliveredAt || order?.completedAt;
}

function orderCancelledTime(order = {}) {
  const t = order?.timings || {};
  return t.cancelledAt || t.canceledAt || order?.cancelledAt || order?.canceledAt || order?.updatedAt;
}

function isCancelledOrder(order = {}) {
  const stage = String(order?.stage || order?.status || '').toLowerCase();
  return ['cancelled', 'canceled'].includes(stage) || order?.cancelled === true || order?.canceled === true;
}

function isCompletedOrder(order = {}) {
  const stage = String(order?.stage || order?.status || '').toLowerCase();
  return ['delivered', 'completed'].includes(stage)
    || Boolean(order?.timings?.deliveredAt || order?.timings?.completedAt || order?.timings?.driverDeliveredAt || order?.deliveredAt || order?.completedAt);
}

function storeStepPeriodTime(order = {}, step = '') {
  const t = order?.timings || {};
  if (step === 'completed') return orderDeliveredTime(order);
  if (step === 'collected') return t.collectedAt || order?.collectedAt || stepTimerStart(order);
  if (step === 'ready') return t.readyForDriverAt || t.packedAt || stepTimerStart(order);
  if (step === 'packing') return t.packingStartedAt || stepTimerStart(order);
  return orderCreatedTime(order);
}

function stepIntervalForPeriod(order = {}, step = '', now = Date.now()) {
  const t = order?.timings || {};
  const sent = safeMs(t.sentToStoreAt || order?.sentToStoreAt || order?.createdAt || order?.placedAt || order?.updatedAt);
  const packing = safeMs(t.packingStartedAt);
  const ready = safeMs(t.readyForDriverAt || t.packedAt);
  const collected = safeMs(t.collectedAt);
  const delivered = safeMs(t.deliveredAt || t.completedAt || t.driverDeliveredAt || order?.deliveredAt || order?.completedAt);
  const activeStep = siteStep(order);
  if (step === 'waiting') return { start: sent, end: packing || (activeStep === 'waiting' ? now : 0) };
  if (step === 'packing') return { start: packing || (activeStep === 'packing' ? safeMs(stepTimerStart(order)) : 0), end: ready || (activeStep === 'packing' ? now : 0) };
  if (step === 'ready') return { start: ready || (activeStep === 'ready' ? safeMs(stepTimerStart(order)) : 0), end: collected || (activeStep === 'ready' ? now : 0) };
  if (step === 'collected') return { start: collected || (activeStep === 'collected' ? safeMs(stepTimerStart(order)) : 0), end: delivered || (activeStep === 'collected' ? now : 0) };
  if (step === 'completed') return { start: delivered, end: delivered };
  return { start: sent, end: sent };
}

function intervalOverlapsRange(interval = {}, range = {}) {
  const start = Number(interval.start || 0);
  const end = Number(interval.end || interval.start || 0);
  if (!start) return false;
  return start <= range.end && end >= range.start;
}

function liveOrderTotal(order = {}) {
  const unavailable = unavailableNames(order);
  const activeOriginalSubtotal = originalOrderItems(order).filter((item, idx) => !unavailable.has(itemKey(item, idx))).reduce((sum, item) => sum + itemPrice(item), 0);
  return activeOriginalSubtotal + calcSwapSubtotal(order) + deliveryFee(order);
}

function liveOrderSubTotal(order = {}) {
  const unavailable = unavailableNames(order);
  return originalOrderItems(order).filter((item, idx) => !unavailable.has(itemKey(item, idx))).reduce((sum, item) => sum + itemPrice(item), 0) + calcSwapSubtotal(order);
}

function canUseAsStoreProfile(profile = {}) {
  const role = normalizeOnTimeRole(profile.onTimeRole || profile.role || '');
  return isActive(profile) && profilePin(profile).length === 6 && role === 'store' && getProfileStores(profile).length > 0;
}

function getProfileStores(profile = {}) {
  const raw = profile.assignedStores || profile.stores || profile.storeAccess || profile.storeNames || profile.store || profile.storeName || profile.site || profile.siteName || profile.assignedStore || profile.location || [];
  if (Array.isArray(raw)) return Array.from(new Set(raw.map((x) => String(x || '').trim()).filter(Boolean))).sort();
  const single = String(raw || '').trim();
  return single ? [single] : [];
}

function getProfileStore(profile = {}) {
  return getProfileStores(profile)[0] || '';
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] || 'L') + (parts[1]?.[0] || 'K');
}

function LinkLogo({ small = false }) {
  return <div className={`link-pwa-logo ${small ? 'small' : ''}`}>Link<span>.</span></div>;
}


function useLinkStoreInstall() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    try {
      document.title = 'Link Site App';
      const manifests = Array.from(document.querySelectorAll('link[rel="manifest"]'));
      if (manifests.length) {
        manifests.forEach((link) => {
          link.setAttribute('data-previous-href', link.getAttribute('href') || '');
          link.setAttribute('data-link-site-manifest', 'true');
          link.setAttribute('href', '/link-site-manifest.webmanifest?v=3');
        });
      } else {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = '/link-site-manifest.webmanifest?v=3';
        link.setAttribute('data-link-site-manifest', 'true');
        document.head.appendChild(link);
      }
      const theme = document.querySelector('meta[name="theme-color"]') || document.createElement('meta');
      theme.setAttribute('name', 'theme-color');
      theme.setAttribute('content', '#020817');
      if (!theme.parentNode) document.head.appendChild(theme);
    } catch (_) {}

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/link-site-sw.js', { scope: '/' }).catch(() => {});
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setPromptEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    if (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone) setInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return false;
    promptEvent.prompt();
    await promptEvent.userChoice.catch(() => null);
    setPromptEvent(null);
    return true;
  }, [promptEvent]);

  return { canInstall: Boolean(promptEvent), installed, install };
}

function InstallPwaButton({ compact = false }) {
  const { canInstall, installed, install } = useLinkStoreInstall();
  const [hint, setHint] = useState('');
  if (installed) return <span className={`link-pwa-install-pill ${compact ? 'compact' : ''}`}>Installed</span>;
  return (
    <button
      type="button"
      className={`link-pwa-install-btn ${compact ? 'compact' : ''}`}
      onClick={async () => {
        if (canInstall) {
          await install();
          return;
        }
        setHint('Install prompt not available yet. Use Chrome menu → Add to Home screen after this page finishes loading.');
        setTimeout(() => setHint(''), 4200);
      }}
    >
      Install app
      {hint ? <small>{hint}</small> : null}
    </button>
  );
}

function StoreEmailLogin({ onReady }) {
  const { user, claims, refreshClaims, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setMessage('');
    clearStoreSession();
    try {
      await ensureSessionPersistence?.();
      const auth = getAuthClient();
      const result = await signInWithEmailAndPassword(auth, email.trim(), password);
      const session = await resolveOnTimeSessionForUser(result.user);
      if (!session?.orgId || !hasOnTimeAccess(session?.claims || {})) {
        await signOut(auth);
        clearStoreSession();
        setMessage('You do not have access to Link. Please contact your MatrixOS admin.');
        return;
      }
      writeOnTimeSession(session);
      clearStoreSession();
      await refreshClaims?.({ force: true });
      pushSiteAuthPath('access');
      onReady?.();
    } catch (error) {
      console.error('[Link Site App] login failed', error);
      setMessage('Email or password is incorrect, or you do not have access to Link. Please contact your admin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="link-pwa-screen link-site-app link-pwa-centered">
      <form className="link-pwa-login-card" onSubmit={handleSubmit}>
        <div className="link-pwa-login-brand-row">
          <div>
            <LinkLogo />
            <div className="link-pwa-kicker">Site App · MatrixOS Entry</div>
          </div>
          <InstallPwaButton compact />
        </div>
        <h1>Sign into Link Site.</h1>
        <p>Use your MatrixOS email first. Then unlock the site queue with the profile PIN.</p>

        <label>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@example.com" autoComplete="username" required />

        <label>Password</label>
        <div className="link-pwa-password-row single">
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" autoComplete="current-password" required />
        </div>

        {message ? <div className="link-pwa-alert">{message}</div> : null}
        <button className="link-pwa-primary-wide" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in to Site App'}</button>
      </form>
    </div>
  );
}

function StoreProfileGate({ onUnlock }) {
  const { user, claims, refreshClaims } = useAuth();
  const { orders, loading: ordersLoading } = useOnTimeLiveData();
  const orgId = String(claims?.orgId || '').trim();
  const [profiles, setProfiles] = useState([]);
  const [settingsStores, setSettingsStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [pin, setPin] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const stores = useMemo(() => {
    const assigned = profiles.filter(canUseAsStoreProfile).flatMap(getProfileStores);
    const allowed = assigned.length ? assigned : settingsStores;
    return [...new Set(allowed.filter(Boolean))].sort();
  }, [profiles, settingsStores]);
  const profileOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return profiles
      .filter(canUseAsStoreProfile)
      .filter((profile) => {
        const boundStores = getProfileStores(profile);
        if (selectedStore && !boundStores.includes(selectedStore)) return false;
        return true;
      })
      .filter((profile) => {
        if (!term) return true;
        return [profileName(profile), profile.email, profile.role, profile.onTimeRole, getProfileStores(profile).join(' ')]
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
  }, [profiles, search, selectedStore]);

  const selectedProfile = useMemo(
    () => profileOptions.find((profile) => String(profile.id) === String(selectedProfileId)) || profileOptions[0] || null,
    [profileOptions, selectedProfileId]
  );

  const storeLoad = useMemo(() => {
    const rows = orders.filter((order) => waitingForStore(order) && (!selectedStore || storeName(order) === selectedStore));
    return {
      waiting: rows.length,
      longest: rows.reduce((max, order) => Math.max(max, minutesSince(order)), 0),
      issues: rows.filter((order) => order.issueFlag || order.stage === 'issue' || order?.flags?.hasStockIssue).length,
    };
  }, [orders, selectedStore]);

  const loadProfiles = useCallback(async () => {
    if (!orgId) return;
    setBusy(true);
    setError('');
    try {
      const [snap, settings] = await Promise.all([
        getDocs(query(collection(db, 'orgs', orgId, 'linkProfiles'), where('active', '==', true))),
        loadOnTimeSettings(orgId),
      ]);
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter(canUseAsStoreProfile);
      setProfiles(rows);
      setSettingsStores(getActiveStoreNames(settings));
    } catch (err) {
      console.error('[Link Site App] profile load failed', err);
      setError('Could not load Link profiles. Check Firestore rules for orgs/{orgId}/linkProfiles.');
    } finally {
      setBusy(false);
    }
  }, [orgId]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  useEffect(() => {
    if (!selectedStore && stores.length) setSelectedStore(stores[0]);
  }, [selectedStore, stores]);

  useEffect(() => {
    if (selectedProfile && String(selectedProfile.id) !== String(selectedProfileId)) setSelectedProfileId(selectedProfile.id);
  }, [selectedProfile, selectedProfileId]);

  async function unlock(e) {
    e.preventDefault();
    setError('');
    if (!selectedStore) {
      setError('Choose a site workspace first.');
      return;
    }
    if (!selectedProfile) {
      setError('Choose a Link site profile first. Create one in Link Admin → Profiles if none appear.');
      return;
    }
    if (cleanPin(pin).length !== 6) {
      setError('Enter the 6-digit site PIN.');
      return;
    }
    if (cleanPin(pin) !== profilePin(selectedProfile)) {
      setError('Incorrect PIN for this site profile.');
      return;
    }

    const session = {
      app: 'link-store',
      orgId,
      uid: user?.uid || '',
      email: user?.email || claims?.email || '',
      store: selectedStore,
      profileId: selectedProfile.id,
      profileName: profileName(selectedProfile),
      role: normalizeOnTimeRole(selectedProfile.onTimeRole || selectedProfile.role || 'store'),
    };
    writeStoreSession(session);
    await refreshClaims?.({ force: false });
    pushSiteAuthPath('home');
    onUnlock(session);
  }

  async function logoutMatrixOS() {
    clearStoreSession();
    try { await signOut(getAuthClient()); } catch (_) {}
  }

  return (
    <div className="link-pwa-screen link-site-app link-pwa-centered">
      <form className="link-pwa-login-card link-pwa-unlock-flow" onSubmit={unlock}>
        <div className="link-pwa-login-topline">
          <div>
            <LinkLogo small />
            <div className="link-pwa-kicker">Site app · Profile unlock</div>
          </div>
          <div className="link-pwa-top-actions">
            <InstallPwaButton compact />
            <button type="button" className="link-pwa-ghost" onClick={logoutMatrixOS}>Logout</button>
          </div>
        </div>
        <label>Site workspace</label>
        <select value={selectedStore} onChange={(e) => setSelectedStore(e.target.value)}>
          {stores.length ? stores.map((store) => <option key={store} value={store}>{store}</option>) : <option value="">No live sites yet</option>}
        </select>
        {!ordersLoading && stores.length === 0 ? <div className="link-pwa-soft-note">No site names found yet. Add sites in Link Admin → Settings first.</div> : null}

        <label>Search / select profile</label>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, role, or site" autoComplete="off" />
        <div className="link-pwa-profile-list">
          {profileOptions.length ? profileOptions.map((profile) => {
            const active = String(selectedProfile?.id) === String(profile.id);
            return (
              <button type="button" key={profile.id} className={active ? 'active' : ''} onClick={() => setSelectedProfileId(profile.id)}>
                <span className="link-pwa-avatar">{getInitials(profileName(profile))}</span>
                <span><b>{profileName(profile)}</b><small>{formatOnTimeRoleLabel(normalizeOnTimeRole(profile.onTimeRole || profile.role))}{getProfileStores(profile).length ? ` · ${getProfileStores(profile).join(', ')}` : ''}</small></span>
              </button>
            );
          }) : <div className="link-pwa-empty-inline">No active PIN profiles found. Create Site/Online Team profiles in Link Admin → Profiles.</div>}
        </div>

        <label>Site PIN</label>
        <input value={pin} onChange={(e) => setPin(cleanPin(e.target.value))} type="password" inputMode="numeric" maxLength={6} placeholder="6-digit PIN" autoComplete="new-password" />

        {error ? <div className="link-pwa-alert">{error}</div> : null}
        <button className="link-pwa-primary-wide" type="submit" disabled={busy || cleanPin(pin).length !== 6}>{busy ? 'Loading…' : 'Enter Site App'}</button>
      </form>
    </div>
  );
}


function OrderLinkChatPage({ order, orgId, session, selectedStore, onBack, onSend, busy, readOnly = false }) {
  const [draft, setDraft] = useState('');
  const [file, setFile] = useState(null);
  const [media, setMedia] = useState(null);
  const [tray, setTray] = useState('');
  const [gifSearch, setGifSearch] = useState('');
  const fileInputId = `link-chat-photo-${order?.id || 'order'}`;
  const messages = useMemo(() => chatMessages(order), [order]);
  const filteredGifs = useMemo(() => {
    const q = gifSearch.trim().toLowerCase();
    return LINK_CHAT_GIFS.filter((item) => !q || item.label.toLowerCase().includes(q) || item.value.toLowerCase().includes(q));
  }, [gifSearch]);

  useEffect(() => {
    if (order?.id) writeChatSeenAt(orgId, order.id, 'store');
  }, [orgId, order?.id, messages.length]);

  async function submit(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text && !file && !media) return;
    if (readOnly) return;
    await onSend(order, { text, file, media });
    setDraft('');
    setFile(null);
    setMedia(null);
    setTray('');
    setGifSearch('');
    window.requestAnimationFrame(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
  }

  function addEmoji(emoji) {
    setDraft((prev) => `${prev}${emoji}`);
    setTray('');
  }

  function pickMedia(item) {
    setMedia(item);
    setTray('');
  }

  return (
    <div className="link-pwa-screen link-site-app link-chat-screen">
      <header className="link-pwa-hero compact link-pwa-store-header">
        <div>
          <LinkLogo small />
          <p>{selectedStore} Site app · signed in as {session?.profileName}</p>
        </div>
        <div className="link-pwa-top-actions">
          <InstallPwaButton compact />
        </div>
      </header>

      <section className="link-pwa-step-page-head link-chat-head">
        <button type="button" className="link-pwa-back-button" onClick={onBack}>← Back</button>
        <div>
          <span>Link chat · Order audit trail</span>
          <h1>#{order.orderNumber || order.orderNo || order.id}</h1>
          <p>{readOnly ? 'Read-only saved conversation for this completed order.' : 'Saved conversation for this order only.'}</p>
        </div>
      </section>

      <main className="link-chat-panel">
        <div className="link-chat-thread">
          {messages.length ? messages.map((msg) => {
            const mine = String(msg.actorRole || '').toLowerCase() === 'store';
            return (
              <div key={msg.id || `${msg.createdAt}-${msg.text}`} className={`link-chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                <div className="link-chat-meta"><b>{msg.actorName || 'Link user'}</b><span>{msg.actorRole || 'link'} · {chatTimeLabel(msg.createdAt)}</span></div>
                {msg.text ? <p>{msg.text}</p> : null}
                {msg.attachmentUrl ? <a className="link-chat-attachment" href={msg.attachmentUrl} target="_blank" rel="noreferrer"><img src={msg.attachmentUrl} alt={msg.attachmentName || 'Chat attachment'} /></a> : null}
                {msg.media && msg.media.type !== 'photo' ? <div className={`link-chat-media-pill ${msg.media.type || ''}`}><span>{msg.media.value || msg.media.label}</span><small>{msg.media.type || 'media'}</small></div> : null}
              </div>
            );
          }) : <div className="link-chat-empty">No messages yet. Start the saved order conversation here.</div>}
        </div>
        {readOnly ? <div className="link-chat-readonly-note">Completed order · chat history is read-only.</div> : <form className="link-chat-compose" onSubmit={submit}>
          {tray ? <div className="link-chat-tray">
            {tray === 'emoji' ? <div className="link-chat-emoji-grid">{LINK_CHAT_EMOJIS.map((emoji) => <button type="button" key={emoji} onClick={() => addEmoji(emoji)}>{emoji}</button>)}</div> : null}
            {tray === 'sticker' ? <div className="link-chat-picker-grid">{LINK_CHAT_STICKERS.map((item) => <button type="button" key={item.label} onClick={() => pickMedia(item)}><b>{item.value}</b><span>{item.label}</span></button>)}</div> : null}
            {tray === 'gif' ? <><input className="link-chat-gif-search" value={gifSearch} onChange={(e) => setGifSearch(e.target.value)} placeholder="Search GIF reaction…" /><div className="link-chat-picker-grid">{filteredGifs.map((item) => <button type="button" key={item.label} onClick={() => pickMedia(item)}><b>{item.value}</b><span>{item.label}</span></button>)}</div></> : null}
          </div> : null}
          {(file || media) ? <div className="link-chat-selected-media">
            {file ? <span>📷 {file.name}</span> : null}
            {media ? <span>{media.value || media.label}</span> : null}
            <button type="button" onClick={() => { setFile(null); setMedia(null); }}>Clear</button>
          </div> : null}
          <div className="link-chat-compose-row">
            <input id={fileInputId} type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0] || null)} hidden />
            <label className="link-chat-tool-button" htmlFor={fileInputId}>📷</label>
            <button className="link-chat-tool-button" type="button" onClick={() => setTray((v) => v === 'emoji' ? '' : 'emoji')}>😊</button>
            <button className="link-chat-tool-button" type="button" onClick={() => setTray((v) => v === 'sticker' ? '' : 'sticker')}>Sticker</button>
            <button className="link-chat-tool-button" type="button" onClick={() => setTray((v) => v === 'gif' ? '' : 'gif')}>GIF</button>
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message Control Tower…" />
            <button type="submit" disabled={!!busy || (!draft.trim() && !file && !media)}>{busy ? 'Sending…' : 'Send'}</button>
          </div>
        </form>}
      </main>
    </div>
  );
}

function StoreQueueWorkspace({ session, onSwitch }) {
  const { orgId, orders, loading } = useOnTimeLiveData();
  const [busy, setBusy] = useState('');
  const initialRoute = useMemo(() => readSiteRoute(), []);
  const [activeStep, setActiveStep] = useState(initialRoute.step || 'waiting');
  const [stepPage, setStepPage] = useState(initialRoute.mode === 'lane' || initialRoute.mode === 'order' ? (initialRoute.step || 'waiting') : '');
  const [selectedDriverByOrder, setSelectedDriverByOrder] = useState({});
  const [deliveryModeByOrder, setDeliveryModeByOrder] = useState({});
  const [now, setNow] = useState(Date.now());
  const [unavailableFor, setUnavailableFor] = useState(null);
  const [swapSearch, setSwapSearch] = useState('');
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelWithdrawFor, setCancelWithdrawFor] = useState(null);
  const [confirmPackingFor, setConfirmPackingFor] = useState(null);
  const [swapQty, setSwapQty] = useState({});
  const [swapPrice, setSwapPrice] = useState({});
  const [expandedOrderId, setExpandedOrderId] = useState(initialRoute.mode === 'order' ? initialRoute.orderId : '');
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [overviewPeriod, setOverviewPeriod] = useState('today');
  const [overviewDate, setOverviewDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [showOverviewCalendar, setShowOverviewCalendar] = useState(false);
  const [products, setProducts] = useState([]);
  const [chatOrderId, setChatOrderId] = useState(initialRoute.mode === 'chat' ? initialRoute.orderId : '');
  const [homeFilter, setHomeFilter] = useState('all');
  const [chatInboxOpen, setChatInboxOpen] = useState(false);
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [menuPanel, setMenuPanel] = useState('');
  const [newPin, setNewPin] = useState('');
  const [pinSaveMessage, setPinSaveMessage] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [activeBottomPanel, setActiveBottomPanel] = useState('orders');
  const [timelineFor, setTimelineFor] = useState(null);
  const selectedStore = session?.store || '';

  useEffect(() => {
    const applyRoute = () => {
      const route = readSiteRoute();
      if (route.mode === 'chat') {
        setChatOrderId(route.orderId || '');
        setExpandedOrderId('');
        return;
      }
      setChatOrderId('');
      if (route.mode === 'order') {
        setExpandedOrderId(route.orderId || '');
        setStepPage(route.step || 'waiting');
        setActiveStep(route.step || 'waiting');
        return;
      }
      if (route.mode === 'lane') {
        setExpandedOrderId('');
        setStepPage(route.step || 'waiting');
        setActiveStep(route.step || 'waiting');
        return;
      }
      setExpandedOrderId('');
      setStepPage('');
      setActiveStep('waiting');
    };
    applyRoute();
    window.addEventListener('popstate', applyRoute);
    return () => window.removeEventListener('popstate', applyRoute);
  }, []);


  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadProducts() {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'orgs', orgId, 'ontimeProducts'));
        if (!alive) return;
        setProducts(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((p) => !productLooksUnavailable(p)));
      } catch (err) {
        console.warn('[Link Site App] products unavailable for swap suggestions', err);
      }
    }
    loadProducts();
    return () => { alive = false; };
  }, [orgId]);

  const allStoreOrders = useMemo(
    () => orders.filter((o) => storeName(o) === selectedStore),
    [orders, selectedStore]
  );

  const siteOrders = useMemo(
    () => allStoreOrders.filter((o) => !isClosedOperationalOrder(o) && isSitePwaOrder(o) && !isDemoOrGhostOrder(o)),
    [allStoreOrders]
  );

  const overviewRange = useMemo(() => periodRange(overviewPeriod, overviewDate), [overviewPeriod, overviewDate]);

  const byStep = useMemo(() => {
    const grouped = { waiting: [], packing: [], ready: [], collected: [], completed: [] };
    siteOrders.forEach((order) => {
      const stage = String(order?.stage || '').toLowerCase();
      const delivered = ['delivered', 'completed'].includes(stage) || order?.timings?.deliveredAt || order?.timings?.completedAt || order?.timings?.driverDeliveredAt;
      const step = delivered ? 'completed' : siteStep(order);
      if (step === 'completed' && !isInRange(orderDeliveredTime(order), overviewRange)) return;
      if (grouped[step]) grouped[step].push(order);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => minutesSince(b, now) - minutesSince(a, now));
    });
    return grouped;
  }, [siteOrders, now, overviewRange]);

  const visibleOrders = byStep[activeStep] || [];
  const hasPendingCancelRequest = useCallback((order = {}) => {
    const raw = order.raw || {};
    const status = String(order.cancelRequestStatus || raw.cancelRequestStatus || order.cancelRequest?.status || raw.cancelRequest?.status || '').toLowerCase();
    return Boolean(order.cancelApprovalRequested || raw.cancelApprovalRequested || order.cancellationRequested || raw.cancellationRequested || status.includes('pending'));
  }, []);
  const requestCancelFromHeader = useCallback(async (order) => {
    if (!order?.id || busy) return;
    if (hasPendingCancelRequest(order)) {
      setCancelWithdrawFor(order);
      return;
    }
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + 'cancel-request', () => storeRequestCancelApproval({ orgId, orderId: order.id, actor }));
  }, [busy, hasPendingCancelRequest, session?.profileName, selectedStore, orgId]);
  const stepPageOrders = useMemo(() => {
    if (!expandedOrderId) return visibleOrders;
    const selected = visibleOrders.filter((order) => String(order.id) === String(expandedOrderId));
    return selected.length ? selected : visibleOrders;
  }, [visibleOrders, expandedOrderId]);
  const liveHomeOrders = useMemo(() => {
    const rows = ['waiting', 'packing', 'ready', 'collected'].flatMap((step) => (byStep[step] || []).map((order) => ({ order, step })));
    const filtered = homeFilter === 'all' ? rows : rows.filter((row) => row.step === homeFilter);
    return filtered.sort((a, b) => minutesSince(b.order, now) - minutesSince(a.order, now));
  }, [byStep, homeFilter, now]);
  const unreadChatOrders = useMemo(() => {
    return siteOrders
      .map((order) => ({ order, count: unreadChatCount(order, orgId, 'store'), latest: chatMessages(order).slice(-1)[0] || null }))
      .filter((row) => row.count > 0)
      .sort((a, b) => safeMs(b.latest?.createdAt) - safeMs(a.latest?.createdAt));
  }, [siteOrders, orgId, now]);
  const unreadChatTotal = unreadChatOrders.reduce((sum, row) => sum + row.count, 0);
  const selectedChatOrder = chatOrderId ? (orders.find((o) => String(o.id) === String(chatOrderId)) || null) : null;
  const shouldCollapseOrders = true;

  const periodStepRows = useMemo(() => {
    const grouped = { waiting: [], packing: [], ready: [], collected: [], completed: [] };
    siteOrders.forEach((order) => {
      ['waiting', 'packing', 'ready', 'collected', 'completed'].forEach((step) => {
        if (intervalOverlapsRange(stepIntervalForPeriod(order, step, now), overviewRange)) grouped[step].push(order);
      });
    });
    return grouped;
  }, [siteOrders, overviewRange, now]);

  const deliveredOrdersInPeriod = useMemo(
    () => allStoreOrders.filter((order) => !isCancelledOrder(order) && isInRange(orderDeliveredTime(order), overviewRange)),
    [allStoreOrders, overviewRange]
  );

  const overallAverage = avgStoreWorkDuration(deliveredOrdersInPeriod, now);
  const stepAverages = useMemo(() => ({
    waiting: avgStepDuration(deliveredOrdersInPeriod, 'waiting', now),
    packing: avgStepDuration(deliveredOrdersInPeriod, 'packing', now),
    ready: avgStepDuration(deliveredOrdersInPeriod, 'ready', now),
    collected: avgStepDuration(deliveredOrdersInPeriod, 'collected', now),
  }), [deliveredOrdersInPeriod, now]);

  const storeOverview = useMemo(() => {
    const createdInPeriod = allStoreOrders.filter((order) => isInRange(orderCreatedTime(order), overviewRange));
    const deliveredInPeriod = deliveredOrdersInPeriod;
    const cancelledInPeriod = allStoreOrders.filter((order) => isCancelledOrder(order) && isInRange(orderCancelledTime(order), overviewRange));
    const cashOrders = deliveredInPeriod.filter((order) => !isCardPayment(order));
    const onlineOrders = deliveredInPeriod.filter(isCardPayment);
    const sum = (rows, fn) => rows.reduce((total, order) => total + Number(fn(order) || 0), 0);
    const deliveredValue = sum(deliveredInPeriod, liveOrderTotal);
    return {
      range: overviewRange,
      totalOrders: createdInPeriod.length,
      delivered: deliveredInPeriod.length,
      cancelled: cancelledInPeriod.length,
      cashOnDelivery: sum(cashOrders, liveOrderTotal),
      cardPayment: sum(onlineOrders, liveOrderTotal),
      onlineExclDelivery: Math.max(0, deliveredValue - sum(deliveredInPeriod, deliveryFee)),
      onlineInclDelivery: deliveredValue,
      deliveredValue,
    };
  }, [allStoreOrders, overviewRange, deliveredOrdersInPeriod]);

  async function action(label, fn) {
    setBusy(label);
    try { await fn(); } catch (error) { alert(error?.message || 'Action failed'); } finally { setBusy(''); }
  }

  async function sendOrderChat(order, payload) {
    if (isCompletedOrder(order)) return;
    const actor = { name: session?.profileName || selectedStore, role: 'store', store: selectedStore };
    const next = typeof payload === 'string' ? { text: payload } : (payload || {});
    await action(order.id + 'chat', () => linkOrderSendMessage({ orgId, orderId: order.id, actor, text: next.text, file: next.file, media: next.media }));
  }

  async function startPacking(order) {
    const hasUnavailable = unavailableNames(order).size > 0;
    if (hasUnavailable) {
      setConfirmPackingFor(order);
      return;
    }
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + 'packing', () => storeStartPacking({ orgId, orderId: order.id, actor }));
    setActiveStep('packing');
    setStepPage('packing');
  }


  async function clearSelectedDriver(order) {
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    setSelectedDriverByOrder((prev) => {
      const next = { ...prev };
      delete next[order.id];
      return next;
    });
    await action(order.id + 'clear-driver', () => siteClearSelectedDriver({ orgId, orderId: order.id, actor }));
  }

  async function selectRequestedDriver(order, request) {
    const currentMode = deliveryMode(order, deliveryModeByOrder[order.id]);
    if (currentMode === 'other') return;
    const driverId = request.driverId || '';
    const driverName = request.driverName || request.driverId || 'driver';
    const selectedKey = selectedDriverByOrder[order.id] || order.assignment?.driverId || order.assignedDriverName || order.assignedDriver || '';
    const requestKey = driverId || driverName || '';
    const alreadySelected = selectedKey && String(selectedKey).toLowerCase() === String(requestKey).toLowerCase();
    if (alreadySelected) {
      await clearSelectedDriver(order);
      return;
    }
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    setSelectedDriverByOrder((prev) => ({ ...prev, [order.id]: driverId || driverName }));
    await action(order.id + 'select-driver', () => siteSelectDriver({
      orgId,
      orderId: order.id,
      driverId,
      driverName,
      actor,
    }));
  }

  function setOrderDeliveryMode(order, mode) {
    setDeliveryModeByOrder((prev) => ({ ...prev, [order.id]: mode }));
    if (mode === 'other') {
      clearSelectedDriver(order);
    }
  }

  async function packedWithPhoto(order, file) {
    if (!file) return;
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + 'packed', () => storePackedWithProof({ file, orgId, orderId: order.id, actor }));
    setActiveStep('ready');
    setStepPage('ready');
  }

  async function collectedWithPhoto(order, file) {
    if (!file) return;
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    const mode = deliveryMode(order, deliveryModeByOrder[order.id]);
    if (mode === 'link' && !hasSelectedLinkDriver(order)) return;
    await action(order.id + 'collected', () => storeMarkCollected({ file, orgId, orderId: order.id, actor, deliveryMode: mode }));
    setActiveStep('collected');
    setStepPage('collected');
  }

  async function completeOtherDriver(order) {
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + 'other-driver-complete', () => storeCompleteOtherDriver({ orgId, orderId: order.id, actor }));
    setActiveStep('completed');
    setStepPage('completed');
  }

  async function toggleUnavailable(order, item, idx) {
    const key = itemKey(item, idx);
    const name = itemName(item);
    const isUnavailable = unavailableNames(order).has(key) || item?.unavailable === true;
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + key, () => isUnavailable
      ? storeReactivateItem({ orgId, orderId: order.id, order, item, itemKey: key, itemName: name, actor })
      : storeMarkItemUnavailable({ orgId, orderId: order.id, order, item, itemKey: key, itemName: name, actor })
    );
  }

  async function addSwapProduct(order, product, replacedItemName = '') {
    const key = product?.id || product?.sku || product?.name || 'swap';
    const qty = Math.max(0.5, Number(swapQty[key] || 1) || 1);
    const customLineTotal = Number(swapPrice[key]);
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + key, () => storeAddSwapProduct({
      orgId,
      orderId: order.id,
      order,
      product,
      qty,
      replacedItemName,
      lineTotalOverride: Number.isFinite(customLineTotal) && customLineTotal >= 0 ? customLineTotal : undefined,
      actor,
    }));
  }

  async function removeSwapProduct(order, item) {
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + (item?.id || itemName(item)) + 'remove-swap', () => storeRemoveSwapProduct({ orgId, orderId: order.id, order, item, actor }));
  }

  async function moveBackToWaiting(order) {
    const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
    await action(order.id + 'back-waiting', () => storeMoveBackToWaiting({ orgId, orderId: order.id, actor }));
    setActiveStep('waiting');
    setStepPage('waiting');
  }


  const tabs = [
    { id: 'waiting', label: 'Waiting' },
    { id: 'packing', label: 'Packing' },
    { id: 'ready', label: 'Ready' },
    { id: 'collected', label: 'Collected' },
    { id: 'completed', label: 'Completed' },
  ];

  const activeTab = tabs.find((tab) => tab.id === activeStep) || tabs[0];

  const liveStepAverages = useMemo(() => {
    const avgFor = (step) => {
      const periodAvg = avgStepDuration(periodStepRows[step] || [], step, now);
      if (periodAvg) return periodAvg;
      return avgStepDuration(byStep[step] || [], step, now);
    };
    return {
      waiting: avgFor('waiting'),
      packing: avgFor('packing'),
      ready: avgFor('ready'),
      collected: avgFor('collected'),
    };
  }, [periodStepRows, byStep, now]);

  const performanceRows = useMemo(() => {
    const rows = {};
    deliveredOrdersInPeriod.forEach((order) => {
      const name = String(order.packerName || order.packedByName || order.assignedPackerName || order.siteUserName || order.storeUserName || session?.profileName || 'Site team').trim();
      if (!rows[name]) rows[name] = { name, orders: 0, totalMins: 0 };
      rows[name].orders += 1;
      rows[name].totalMins += avgStoreWorkDuration([order], now) || orderStepDurations(order, now).total || 0;
    });
    return Object.values(rows).sort((a, b) => b.orders - a.orders);
  }, [deliveredOrdersInPeriod, now, session?.profileName]);

  const allHomeRows = useMemo(() => ['waiting', 'packing', 'ready', 'collected'].flatMap((step) => (byStep[step] || []).map((order) => ({ order, step }))), [byStep]);

  const timelineEventsFor = useCallback((order) => {
    const t = order?.timings || {};
    const hasSwap = swapOrderItems(order).length > 0 || order?.swapRequested || order?.swapApproved;
    const events = [
      ['Order received', t.sentToStoreAt || t.storeReceivedAt || t.orderReceivedAt || order?.sentToStoreAt || order?.placedAt || order?.createdAt || getTime(order)],
      ...(unavailableNames(order).size && t.unavailableMarkedAt ? [['Unavailable marked', t.unavailableMarkedAt]] : []),
      ...(hasSwap && t.swapApprovalStartedAt ? [['Swap requested', t.swapApprovalStartedAt]] : []),
      ...(hasSwap && (t.swapApprovedAt || order?.swapApprovedAt) ? [['Swap approved', t.swapApprovedAt || order?.swapApprovedAt]] : []),
      ['Packing started', t.packingStartedAt],
      ['Ready for driver', t.readyForDriverAt || t.packedAt],
      ['Collected', t.collectedAt],
      ['Delivered', t.deliveredAt || t.completedAt || t.driverDeliveredAt],
    ];
    return events.filter(([, raw]) => safeMs(raw)).map(([label, raw]) => ({ label, at: new Date(safeMs(raw)) }));
  }, []);

  const operatorFirstName = String(session?.profileName || session?.displayName || 'there').trim().split(/\s+/)[0] || 'there';
  const openStepPage = (step) => {
    setActiveStep(step);
    setStepPage(step);
    setExpandedOrderId('');
    setChatOrderId('');
    setUnavailableFor(null);
    pushSiteRoute({ mode: 'lane', step });
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };
  const closeStepPage = () => {
    setStepPage('');
    setExpandedOrderId('');
    setChatOrderId('');
    setUnavailableFor(null);
    pushSiteRoute({ mode: 'home' });
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  async function saveProfilePin() {
    const pin = cleanPin(newPin);
    setPinSaveMessage('');
    if (pin.length !== 6) { setPinSaveMessage('Enter a 6-digit PIN.'); return; }
    if (!session?.profileId || !orgId) { setPinSaveMessage('Profile session not found. Switch user and try again.'); return; }
    setPinSaving(true);
    try {
      await updateDoc(doc(db, 'orgs', orgId, 'linkProfiles', session.profileId), { accessPin: pin, pin, updatedAt: serverTimestamp() });
      setPinSaveMessage('PIN updated. Use the new PIN next time.');
      setNewPin('');
    } catch (error) {
      console.warn('[Link Site App] PIN update failed', error);
      setPinSaveMessage('Could not update PIN. Check permissions or ask your admin.');
    } finally {
      setPinSaving(false);
    }
  }

  if (selectedChatOrder) {
    return <OrderLinkChatPage order={selectedChatOrder} orgId={orgId} session={session} selectedStore={selectedStore} busy={busy} readOnly={isCompletedOrder(selectedChatOrder)} onBack={() => { setChatOrderId(''); pushSiteRoute(expandedOrderId ? { mode: 'order', orderId: expandedOrderId, step: stepPage || activeStep } : { mode: 'home' }); }} onSend={sendOrderChat} />;
  }

  return (
    <div className={`link-pwa-screen link-site-app ${stepPage ? 'is-step-page' : ''}`}>
      <header className="link-pwa-hero compact link-pwa-store-header">
        <div>
          <LinkLogo small />
          <p>{selectedStore} Site app · signed in as {session?.profileName}</p>
        </div>
        <div className="link-pwa-top-actions">
          <InstallPwaButton compact />
          <button className="link-pwa-ghost" onClick={onSwitch}>Switch site/profile</button>
        </div>
      </header>

      {!stepPage ? (
        <div className="link-store-native-home">
          <section className="link-store-mobile-top">
            <button type="button" className="link-store-menu-dot" aria-label="Open menu" onClick={() => setStoreMenuOpen((v) => !v)}>☰</button>
            <div>
              <LinkLogo small />
              <small>Hello {operatorFirstName}</small>
            </div>
            <button
              type="button"
              className={`link-store-bell ${unreadChatTotal ? 'has-unread' : ''}`}
              aria-label="Order chat notifications"
              onClick={() => {
                if (unreadChatOrders.length === 1) {
                  setChatOrderId(unreadChatOrders[0].order.id); pushSiteRoute({ mode: 'chat', orderId: unreadChatOrders[0].order.id });
                  writeChatSeenAt(orgId, unreadChatOrders[0].order.id, 'store');
                  setChatInboxOpen(false);
                } else {
                  setChatInboxOpen((open) => !open);
                }
              }}
            >🔔{unreadChatTotal ? <em>{unreadChatTotal}</em> : null}</button>
          </section>
          {storeMenuOpen ? (
            <>
              <button type="button" className="link-store-menu-scrim" aria-label="Close menu" onClick={() => { setStoreMenuOpen(false); setMenuPanel(''); }} />
              <section className="link-store-menu-panel">
                <button type="button" onClick={() => { setStoreMenuOpen(false); setMenuPanel(''); onSwitch(); }}>Switch user / site</button>
                <button type="button" onClick={() => setMenuPanel((v) => v === 'profile' ? '' : 'profile')}>Profile</button>
                {menuPanel === 'profile' ? (
                  <div className="link-store-menu-detail">
                    <b>{session?.profileName || 'Site user'}</b>
                    <small>{session?.email || ''}</small>
                    <label>New PIN</label>
                    <input value={newPin} onChange={(e) => setNewPin(cleanPin(e.target.value))} inputMode="numeric" maxLength={6} placeholder="6-digit PIN" />
                    <button type="button" className="mini-action" disabled={pinSaving || cleanPin(newPin).length !== 6} onClick={saveProfilePin}>{pinSaving ? 'Saving…' : 'Update PIN'}</button>
                    {pinSaveMessage ? <small className="menu-note">{pinSaveMessage}</small> : null}
                  </div>
                ) : null}
                <button type="button" onClick={() => setMenuPanel((v) => v === 'help' ? '' : 'help')}>Help</button>
                {menuPanel === 'help' ? (
                  <div className="link-store-menu-detail">
                    <b>Need help?</b>
                    <small>Contact your Link admin or Control Tower for PINs, access, order issues, swaps, and cancellation approvals.</small>
                  </div>
                ) : null}
                <button type="button" onClick={() => setMenuPanel((v) => v === 'about' ? '' : 'about')}>About Link</button>
                {menuPanel === 'about' ? (
                  <div className="link-store-menu-detail">
                    <b>Link Site</b>
                    <small>Version 1.0 · © 2026 MatrixOS</small>
                    <small>Accept · Pack · Prove · Handover</small>
                  </div>
                ) : null}
                <button type="button" onClick={() => { clearStoreSession(); signOut(getAuthClient()).finally(() => { setStoreMenuOpen(false); setMenuPanel(''); pushSiteAuthPath('login'); window.location.reload(); }); }}>Logout</button>
              </section>
            </>
          ) : null}
          {chatInboxOpen ? (
            <section className="link-store-chat-inbox">
              <div className="chat-inbox-head"><b>Order messages</b><button type="button" onClick={() => setChatInboxOpen(false)}>Close</button></div>
              {unreadChatOrders.length ? unreadChatOrders.map(({ order, count, latest }) => (
                <button key={order.id} type="button" onClick={() => { setChatOrderId(order.id); pushSiteRoute({ mode: 'chat', orderId: order.id }); writeChatSeenAt(orgId, order.id, 'store'); setChatInboxOpen(false); }}>
                  <span>#{order.orderNumber || order.orderNo || order.id}</span>
                  <small>{count} new · {latest?.text || latest?.media ? (latest.media.type === 'photo' ? 'Photo uploaded' : latest.media.label) : 'New message'}</small>
                </button>
              )) : <p>No unread order messages.</p>}
            </section>
          ) : null}

          <section className="link-store-site-card">
            <div className="link-store-site-head">
              <div>
                <span className="online-dot" />
                <h1>{selectedStore} Store</h1>
                <p>{session?.storeAddress || 'Live order fulfilment'}</p>
              </div>
              <button type="button" className="link-store-online-pill">⌁ Online</button>
            </div>
            <div className="link-store-stage-grid">
              {tabs.map((tab) => {
                const cardCount = tab.id === 'completed' ? storeOverview.delivered : (byStep[tab.id] || []).length;
                const stageIcon = tab.id === 'waiting' ? '⌛' : tab.id === 'packing' ? '⬡' : tab.id === 'ready' ? '✓' : tab.id === 'collected' ? '▰' : '✓';
                return (
                  <div key={tab.id} className={`store-stage-tile ${tab.id}`} aria-label={`${tab.label} ${cardCount}`}>
                    <i>{stageIcon}</i>
                    <span>{tab.id === 'completed' ? "Today's Deliveries" : tab.label}</span>
                    <strong>{cardCount}</strong>
                    <small>{tab.id === 'completed' ? `${money(storeOverview.deliveredValue || 0)} today` : `Avg ${fmtMins(liveStepAverages[tab.id] || 0)}`}</small>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="link-store-live-orders-head">
            <h2>Live Orders</h2>
          </section>
          <div className="link-store-filter-tabs">
            {[{ id:'all', label:'All', count: allHomeRows.length }, ...tabs.filter((t) => t.id !== 'completed').map((t) => ({ id:t.id, label:t.label, count:(byStep[t.id] || []).length }))].map((tab) => (
              <button key={tab.id} type="button" className={homeFilter === tab.id ? 'active' : ''} onClick={() => setHomeFilter(tab.id)}>
                {tab.label} <b>{tab.count}</b>
              </button>
            ))}
          </div>

          <section className="link-store-order-feed">
            {loading ? <div className="link-store-empty">Loading live orders…</div> : null}
            {!loading && liveHomeOrders.length === 0 ? <div className="link-store-empty">No live orders for {selectedStore}.</div> : null}
            {liveHomeOrders.map(({ order, step }) => {
              const mins = orderStepDurations(order, now);
              const feedMins = mins.total ?? minutesSince(order, now);
              return (
                <button type="button" key={order.id} className={`link-store-feed-card ${step}`} onClick={() => { setActiveStep(step); setStepPage(step); setExpandedOrderId(order.id); pushSiteRoute({ mode: 'order', orderId: order.id, step }); window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' })); }}>
                  <span className="feed-color" />
                  <div className="feed-main">
                    <h3>#{order.orderNumber || order.orderNo || order.id}</h3>
                    <p>{customerDisplay(order)}</p>
                    <small>◷ {fmtMins(feedMins)} · {new Date(getTime(order)).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</small>
                  </div>
                  <div className="feed-side">
                    <em>{step === 'collected' ? 'Driver' : step}</em>
                    <strong>{money(liveOrderTotal(order))}</strong>
                    <small>{paymentMethod(order)}</small>
                  </div>
                  <i>›</i>
                </button>
              );
            })}
          </section>


          {activeBottomPanel === 'performance' ? (
            <div className="link-store-panel-scrim" onClick={() => setActiveBottomPanel('orders')}>
            <section className="link-store-performance-panel" onClick={(e) => e.stopPropagation()}>
              <div className="panel-head"><b>Site performance</b></div>
              <div className="perf-grid">
                <span><small>Delivered today</small><b>{storeOverview.delivered}</b></span>
                <span><small>COD</small><b>{money(storeOverview.cashOnDelivery)}</b></span>
                <span><small>Card</small><b>{money(storeOverview.cardPayment)}</b></span>
                <span><small>Delivered total</small><b>{money(storeOverview.deliveredValue || (storeOverview.cashOnDelivery + storeOverview.cardPayment))}</b></span>
                <span><small>Excl delivery</small><b>{money(storeOverview.onlineExclDelivery)}</b></span>
                <span><small>Avg time</small><b>{fmtMins(overallAverage || 0)}</b></span>
              </div>
              <div className="perf-team">
                <b>Team on shift</b>
                {performanceRows.length ? performanceRows.map((row) => <p key={row.name}><span>{row.name}</span><strong>{row.orders} orders</strong></p>) : <p><span>{session?.profileName || 'Site team'}</span><strong>0 orders</strong></p>}
              </div>
            </section>
            </div>
          ) : null}
          {activeBottomPanel === 'inventory' ? (
            <div className="link-store-panel-scrim" onClick={() => setActiveBottomPanel('orders')}>
            <section className="link-store-performance-panel inventory-note" onClick={(e) => e.stopPropagation()}>
              <div className="panel-head"><b>Inventory source</b></div>
              <p>Coming next: connect this store to Morpheus so unavailable items are set before orders arrive.</p>
            </section>
            </div>
          ) : null}
          <nav className="link-store-bottom-nav three">
            <button className={activeBottomPanel === 'orders' ? 'active' : ''} onClick={() => setActiveBottomPanel('orders')}>▢<span>Orders</span></button>
            <button className={activeBottomPanel === 'performance' ? 'active' : ''} onClick={() => setActiveBottomPanel((v) => v === 'performance' ? 'orders' : 'performance')}>▰<span>Performance</span></button>
            <button className={activeBottomPanel === 'inventory' ? 'active' : ''} onClick={() => setActiveBottomPanel((v) => v === 'inventory' ? 'orders' : 'inventory')}>⬡<span>Inventory</span></button>
          </nav>
        </div>
      ) : (
        <section className="link-pwa-step-page-head link-pwa-detail-head">
          <button type="button" className="link-pwa-back-button" onClick={closeStepPage}>← Back</button>
          <div>
            <span>{selectedStore} · {activeTab.label}</span>
            <h1>{activeTab.label}</h1>
            <p>{visibleOrders.length} {visibleOrders.length === 1 ? 'order' : 'orders'} in this lane.</p>
          </div>
          {visibleOrders.length === 1 && activeStep !== 'completed' ? (() => {
            const pendingCancel = hasPendingCancelRequest(visibleOrders[0]);
            return <button type="button" className={`link-pwa-cancel-request-top ${pendingCancel ? 'requesting' : ''}`} onClick={() => requestCancelFromHeader(visibleOrders[0])} disabled={!!busy}>{pendingCancel ? 'Requesting' : 'Request cancel'}</button>;
          })() : null}
        </section>
      )}

      {stepPage ? (
      <main className="link-pwa-order-stack link-pwa-step-page-stack">
        {loading ? <div className="link-pwa-empty">Loading linked queue…</div> : null}
        {!loading && visibleOrders.length === 0 ? <div className="link-pwa-empty">No {activeStep} orders for {selectedStore}.</div> : null}
        {stepPageOrders.map((order) => {
          const step = siteStep(order);
          const issue = order.issueFlag || order.stage === 'issue' || order?.flags?.hasStockIssue;
          const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore };
          const packedPhoto = order?.proof?.packedPhotoUrl || order?.storeProof?.packedPhotoUrl || '';
          const collectedPhoto = order?.proof?.collectedPhotoUrl || order?.storeProof?.collectedPhotoUrl || '';
          const durations = orderStepDurations(order, now);
          const unavailable = unavailableNames(order);
          const originalSubtotal = calcOriginalSubtotal(order);
          const activeOriginalSubtotal = originalOrderItems(order).filter((item, idx) => !unavailable.has(itemKey(item, idx))).reduce((sum, item) => sum + itemPrice(item), 0);
          const swapSubtotal = calcSwapSubtotal(order);
          const delivery = deliveryFee(order);
          const paidTotal = originalPaidTotal(order, originalSubtotal, delivery);
          const liveTotal = activeOriginalSubtotal + swapSubtotal + delivery;
          const balance = liveTotal - paidTotal;
          const paidByCard = isCardPayment(order);
          const hasAdjustments = unavailable.size > 0 || swapOrderItems(order).length > 0;
          const awaitingApproval = hasAdjustments && !order?.swapApproved;
          const approvalMins = approvalWaitMinutes(order, now);
          const displayStep = activeStep === 'completed' ? 'completed' : step;
          const finalOrderTotal = liveTotal;
          const isCollapsed = shouldCollapseOrders && expandedOrderId !== order.id;
          if (isCollapsed) {
            return (
              <article
                key={order.id}
                role="button"
                tabIndex={0}
                onClick={() => setExpandedOrderId((current) => { const next = current === order.id ? '' : order.id; pushSiteRoute(next ? { mode: 'order', orderId: next, step: stepPage || activeStep } : { mode: 'lane', step: stepPage || activeStep }); return next; })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedOrderId((current) => { const next = current === order.id ? '' : order.id; pushSiteRoute(next ? { mode: 'order', orderId: next, step: stepPage || activeStep } : { mode: 'lane', step: stepPage || activeStep }); return next; }); } }}
                className={`link-pwa-order-card readable collapsed-order-card step-${displayStep} ${issue ? 'has-issue' : ''}`}
              >
                <div>
                  <span className="link-pwa-stage">{storeStageLabel(displayStep, order, issue)}</span>
                  <h2>#{order.orderNumber || order.orderNo || order.id}</h2>
                  <p>Sent by <b>{onlineSender(order)}</b> · {onlineTeamLabel(order)}</p>
                </div>
                <div className="collapsed-order-summary">
                  <strong>{fmtMins(durations.total || 0)}</strong>
                  <span>Tap to extend</span>
                  <em>{money(finalOrderTotal)}</em>
                </div>
              </article>
            );
          }
          return (
            <article key={order.id} className={`link-pwa-order-card readable step-${displayStep} open ${issue ? 'has-issue' : ''}`}>
              <div className="link-pwa-order-top" onClick={() => setExpandedOrderId((current) => { const next = current === order.id ? '' : order.id; pushSiteRoute(next ? { mode: 'order', orderId: next, step: stepPage || activeStep } : { mode: 'lane', step: stepPage || activeStep }); return next; })} role="button" tabIndex={0}>
                <div>
                  <span className="link-pwa-stage">{storeStageLabel(displayStep, order, issue)}</span>
                  <div className="link-pwa-order-title-row">
                    <h2>#{order.orderNumber || order.orderNo || order.id}</h2>
                    <button type="button" className={`link-order-chat-button ${unreadChatCount(order, orgId, 'store') ? 'has-unread' : ''}`} onClick={(e) => { e.stopPropagation(); setChatOrderId(order.id); pushSiteRoute({ mode: 'chat', orderId: order.id }); writeChatSeenAt(orgId, order.id, 'store'); }}>
                      Chat{unreadChatCount(order, orgId, 'store') ? ` ${unreadChatCount(order, orgId, 'store')}` : ''}
                    </button>
                  </div>
                  <p>Sent by <b>{onlineSender(order)}</b> · {onlineTeamLabel(order)}</p>
                </div>
                <div className="link-pwa-timer-cluster stages" onClick={(e) => { e.stopPropagation(); setTimelineFor(order); }} role="button" title="Open order timeline">
                  <div className="link-pwa-timer"><small>Waiting</small><strong>{fmtMins(durations.waiting || 0)}</strong>{awaitingApproval ? <em>Approval wait {fmtMins(approvalMins)}</em> : null}</div>
                  <div className="link-pwa-timer muted"><small>Packing</small><strong>{durations.packing == null ? '—' : fmtMins(durations.packing)}</strong></div>
                  <div className="link-pwa-timer muted"><small>Ready</small><strong>{durations.ready == null ? '—' : fmtMins(durations.ready)}</strong></div>
                  <div className="link-pwa-timer muted"><small>Collected</small><strong>{durations.collected == null ? '—' : fmtMins(durations.collected)}</strong></div>
                  <div className="link-pwa-timer total"><small>Total</small><strong>{fmtMins(durations.total || 0)}</strong></div>
                </div>
              </div>

              <section className="link-pwa-customer-block">
                <span>Customer</span>
                <strong>{customerDisplay(order)}</strong>
                <p>{order.phone || order.customerPhone || order.customer?.phone || ''}</p>
                <p>{order.address || order.deliveryAddress || order.customer?.address || ''}</p>
                {(order.customerNote || order.notes?.customer || order.instructions) ? (
                  <div className="link-pwa-instructions"><b>Instructions</b><br />{order.customerNote || order.notes?.customer || order.instructions}</div>
                ) : null}
              </section>

              <section className="link-pwa-order-detail-split">
                <div className="link-pwa-pack-list">
                  <span>Pack list</span>
                  {originalOrderItems(order).length ? originalOrderItems(order).map((item, idx) => {
                    const key = itemKey(item, idx);
                    const off = unavailable.has(key);
                    return (
                      <p key={`${key}-${idx}`} className={off ? 'unavailable' : ''}>
                        <span className="item-left"><b>{itemQty(item)} ×</b> {itemName(item)}{off ? <small>Unavailable</small> : null}</span>
                        <strong>{money(itemPrice(item))}</strong>
                      </p>
                    );
                  }) : <p>No items attached.</p>}
                  {swapOrderItems(order).length ? (
                    <div className="link-pwa-pack-additions">
                      <span>Additions</span>
                      {swapOrderItems(order).map((item, idx) => (
                        <p key={`swap-${item.id || idx}`} className="swap-line">
                          <span className="item-left"><b>{itemQty(item)} ×</b> {itemName(item)}<small>Addition pending approval</small></span>
                          <strong>{money(itemPrice(item))}</strong>
                          {step === 'waiting' ? <button type="button" onClick={() => removeSwapProduct(order, item)}>Remove</button> : null}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="link-pwa-totals-card">
                  <span>Totals</span>
                  <p><b>Original paid/order total</b><strong>{money(paidTotal)}</strong></p>
                  <p><b>Live pack sub-total</b><strong>{money(activeOriginalSubtotal + swapSubtotal)}</strong></p>
                  <p><b>Delivery</b><strong>{money(delivery)}</strong></p>
                  <p className="grand"><b>{paidByCard ? 'Swap adjusted total' : 'Total to collect'}</b><strong>{money(liveTotal)}</strong></p>
                  <p><b>Payment</b><strong>{paymentMethod(order)}</strong></p>
                  {hasAdjustments ? <p className={balance === 0 ? 'balance even' : balance > 0 ? 'balance collect' : 'balance refund'}><b>{balance === 0 ? 'Difference' : balance > 0 ? 'Customer owes' : 'We owe customer'}</b><strong>{balance < 0 ? `-${money(Math.abs(balance))}` : money(balance)}</strong></p> : null}
                  {hasAdjustments ? <p className="approval-note">Swap/removal needs online approval before packing.</p> : null}
                </div>
              </section>

              {unavailableFor?.id === order.id ? (
                <section className="link-pwa-unavailable-panel">
                  <div className="panel-head"><b>Set unavailable items</b><button type="button" onClick={() => setUnavailableFor(null)}>Close</button></div>
                  <div className="link-pwa-unavailable-items">
                    {originalOrderItems(order).map((item, idx) => {
                      const key = itemKey(item, idx);
                      const off = unavailable.has(key);
                      return <button key={`${key}-unavailable`} type="button" className={`link-pwa-unavailable-toggle ${off ? 'is-off' : ''}`} onClick={() => toggleUnavailable(order, item, idx)}>
                        <span className="unavailable-action">{off ? 'Reactivate' : 'Mark unavailable'}</span>
                        <span className="unavailable-dot">·</span>
                        <span className="unavailable-product">{itemName(item)}</span>
                      </button>;
                    })}
                  </div>
                  <div className="link-pwa-swap-panel always-visible">
                    <b>Swap / add from Link products</b>
                    <p>Unavailable items stay visible until reactivated. Search products below to add additions for online approval.</p>
                    <input className="link-pwa-product-search" value={swapSearch} onChange={(e) => setSwapSearch(e.target.value)} placeholder="Search product to add…" />
                    <div>{products.length && swapSearch.trim() ? products
                      .filter((p) => !productLooksUnavailable(p) && !productMatchesUnavailable(p, unavailable))
                      .filter((p) => String(p.name || p.productName || p.title || '').toLowerCase().includes(swapSearch.trim().toLowerCase()))
                      .slice(0, 6).map((p) => {
                      const pKey = p.id || p.sku || p.name || p.productName || p.title;
                      const pName = p.name || p.productName || p.title || 'Product';
                      const qty = swapQty[pKey] || 1;
                      const defaultPrice = productPrice(p, qty);
                      const priceValue = swapPrice[pKey] ?? defaultPrice;
                      return <span className="link-pwa-swap-choice editable" key={p.id || pName}>
                        <button type="button" onClick={() => { addSwapProduct(order, { ...p, qty, quantity: qty, lineTotal: Number(priceValue) || defaultPrice, price: (Number(priceValue) || defaultPrice) / Math.max(0.5, Number(qty) || 1), unitPrice: (Number(priceValue) || defaultPrice) / Math.max(0.5, Number(qty) || 1) }, unavailable.size ? [...unavailable][0] : ''); setSwapSearch(''); }}>Add · {pName}</button>
                        <label>Qty
                          <input type="number" min="0.5" step="0.5" value={qty} onChange={(e) => setSwapQty((prev) => ({ ...prev, [pKey]: e.target.value }))} aria-label={`Quantity for ${pName}`} />
                        </label>
                        <label>Price
                          <input type="number" min="0" step="1" value={priceValue} onChange={(e) => setSwapPrice((prev) => ({ ...prev, [pKey]: e.target.value }))} aria-label={`Price for ${pName}`} />
                        </label>
                      </span>;
                    }) : null}</div>
                  </div>
                </section>
              ) : null}

              {displayStep !== 'completed' && (packedPhoto || collectedPhoto) ? (
                <div className="link-pwa-proof-row">
                  {packedPhoto ? <a href={packedPhoto} target="_blank" rel="noreferrer">Packed proof photo</a> : null}
                  {collectedPhoto ? <a href={collectedPhoto} target="_blank" rel="noreferrer">Collected proof photo</a> : null}
                </div>
              ) : null}


              {displayStep === 'ready' && pendingDriverRequests(order).length ? (() => {
                const mode = deliveryMode(order, deliveryModeByOrder[order.id]);
                const linkDriversDisabled = mode === 'other';
                return (
                  <section className={`link-pwa-driver-requests ${linkDriversDisabled ? 'drivers-disabled' : ''}`}>
                    <span>{linkDriversDisabled ? 'Link drivers disabled · Other Driver selected' : 'Driver requests'}</span>
                    {pendingDriverRequests(order).map((request) => {
                      const selectedKey = selectedDriverByOrder[order.id] || order.assignment?.driverId || order.assignedDriverName || order.assignedDriver || '';
                      const requestKey = request.driverId || request.driverName || '';
                      const selected = !linkDriversDisabled && selectedKey && String(selectedKey).toLowerCase() === String(requestKey).toLowerCase();
                      return (
                        <button key={`${request.driverId || request.driverName}`} type="button" className={selected ? 'selected-driver-request' : ''} onClick={() => selectRequestedDriver(order, request)} disabled={!!busy || linkDriversDisabled} title={selected ? 'Click again to unselect this driver' : ''}>
                          {selected ? 'Selected · ' : 'Select '}{request.driverName || 'driver'}
                        </button>
                      );
                    })}
                  </section>
                );
              })() : null}

              {displayStep !== 'completed' ? (
                <div className={`link-pwa-actions single-flow three-zone action-step-${displayStep}`}>
                  {displayStep === 'waiting' ? <div className="start-pack-stack"><button className={`primary start-left ${awaitingApproval ? 'approval-waiting-button' : ''}`} onClick={() => startPacking(order)} disabled={!!busy || awaitingApproval}>{awaitingApproval ? `Waiting for approval · ${fmtMins(approvalMins)}` : 'Start packing'}</button></div> : null}
                  {displayStep === 'waiting' ? <div className="center-action-stack"><button className="center-action" onClick={() => setUnavailableFor(unavailableFor?.id === order.id ? null : order)}>Set unavailable items</button></div> : null}
                  {displayStep === 'packing' ? (
                    <label className={`link-pwa-photo-action start-left ${busy ? 'disabled' : ''}`}>Packed · photo required<input type="file" accept="image/*" capture="environment" disabled={!!busy} onChange={(e) => packedWithPhoto(order, e.target.files?.[0])} /></label>
                  ) : null}
                  {displayStep === 'ready' ? (() => {
                    const mode = deliveryMode(order, deliveryModeByOrder[order.id]);
                    const selectedLinkDriver = mode === 'link' && Boolean(selectedDriverByOrder[order.id] || hasSelectedLinkDriver(order));
                    return (
                      <div className="link-pwa-handoff-row start-left">
                        <div className="link-pwa-driver-mode-toggle" role="group" aria-label="Delivery driver mode">
                          <button type="button" className={mode === 'link' ? 'active' : ''} onClick={() => setOrderDeliveryMode(order, 'link')}>Link Driver</button>
                          <button type="button" className={mode === 'other' ? 'active other' : ''} onClick={() => setOrderDeliveryMode(order, 'other')}>Other Driver</button>
                        </div>
                        <label className={`link-pwa-photo-action amber ${busy || (mode === 'link' && !selectedLinkDriver) ? 'disabled' : ''}`}>
                          {mode === 'link' && !selectedLinkDriver ? 'Select Link driver first' : 'Collected · photo required'}
                          <input type="file" accept="image/*" capture="environment" disabled={!!busy || (mode === 'link' && !selectedLinkDriver)} onChange={(e) => collectedWithPhoto(order, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })() : null}
                  {displayStep === 'collected' ? (deliveryMode(order, deliveryModeByOrder[order.id]) === 'other' ? <button type="button" className="link-pwa-collected-done active-complete start-left" onClick={() => completeOtherDriver(order)} disabled={!!busy}>Complete delivery</button> : <span className="link-pwa-collected-done start-left">Waiting for driver</span>) : null}
                </div>
              ) : null}
            </article>
          );
        })}
      </main>
      ) : null}

      {timelineFor ? (
        <div className="link-pwa-modal-backdrop">
          <div className="link-pwa-modal-card timeline">
            <h3>Order timeline</h3>
            <p>#{timelineFor.orderNumber || timelineFor.orderNo || timelineFor.id}</p>
            <div className="link-order-timeline clean">
              {timelineEventsFor(timelineFor).map((ev) => <div key={ev.label}><span>{ev.label}</span><strong>{ev.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></div>)}
              {!timelineEventsFor(timelineFor).length ? <p><span>No timeline events yet</span></p> : null}
            </div>
            <div><button onClick={() => setTimelineFor(null)}>Close</button></div>
          </div>
        </div>
      ) : null}

      {confirmPackingFor ? (
        <div className="link-pwa-modal-backdrop">
          <div className="link-pwa-modal-card">
            <h3>Pack with unavailable items?</h3>
            <p>This order has unavailable items. Continue only if the online team approved the swap or removal.</p>
            <div><button onClick={() => setConfirmPackingFor(null)}>Cancel</button><button className="primary" onClick={async () => { const order = confirmPackingFor; setConfirmPackingFor(null); const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore }; await action(order.id + 'packing', () => storeStartPacking({ orgId, orderId: order.id, actor })); setActiveStep('packing'); setStepPage('packing'); }}>Continue packing</button></div>
          </div>
        </div>
      ) : null}

      {cancelFor ? (
        <div className="link-pwa-modal-backdrop">
          <div className="link-pwa-modal-card danger">
            <h3>Request cancellation?</h3>
            <p>This will keep the order live and send a cancellation approval request to Control Tower.</p>
            <div><button onClick={() => setCancelFor(null)}>Keep order</button><button className="danger" onClick={async () => { const order = cancelFor; setCancelFor(null); const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore }; await action(order.id + 'cancel-request', () => storeRequestCancelApproval({ orgId, orderId: order.id, actor })); }}>Send request</button></div>
          </div>
        </div>
      ) : null}

      {cancelWithdrawFor ? (
        <div className="link-pwa-modal-backdrop">
          <div className="link-pwa-modal-card">
            <h3>Stop cancellation request?</h3>
            <p>This order will stay live and Control Tower will no longer see it as requesting cancellation.</p>
            <div><button onClick={() => setCancelWithdrawFor(null)}>Keep requesting</button><button className="primary" onClick={async () => { const order = cancelWithdrawFor; setCancelWithdrawFor(null); const actor = { name: session?.profileName || selectedStore, role: 'site', store: selectedStore }; await action(order.id + 'cancel-withdraw', () => storeWithdrawCancelApproval({ orgId, orderId: order.id, actor })); }}>Do not cancel anymore</button></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function LinkStorePwaPage() {
  const { user, claims, loading } = useAuth();
  const [session, setSession] = useState(() => readStoreSession());
  const [emailEntryReady, setEmailEntryReady] = useState(false);
  const orgId = String(claims?.orgId || '').trim();
  const routeMode = readSiteRoute().mode;
  const sessionValid = Boolean(
    orgId &&
    session?.orgId === orgId &&
    session?.store &&
    session?.profileId &&
    (!user || !session?.uid || session.uid === user.uid)
  );

  if (loading) {
    return <div className="link-pwa-screen link-pwa-centered"><div className="link-pwa-empty">Loading Link Site App…</div></div>;
  }

  // Explicit URL states for scalable PWA navigation:
  // /site/login  -> email login
  // /site/access -> profile/PIN unlock
  // /site        -> protected site workspace
  if (routeMode === 'login') {
    return <StoreEmailLogin onReady={() => { setSession(null); setEmailEntryReady(true); }} />;
  }

  if (routeMode === 'access') {
    if (!user || !orgId) return <StoreEmailLogin onReady={() => { setSession(null); setEmailEntryReady(true); }} />;
    return <StoreProfileGate onUnlock={(next) => setSession(next)} />;
  }

  if (!sessionValid && !emailEntryReady) {
    pushSiteAuthPath('login');
    return <StoreEmailLogin onReady={() => { setSession(null); setEmailEntryReady(true); }} />;
  }

  if (!sessionValid && (!user || !orgId) && !session?.orgId) {
    pushSiteAuthPath('login');
    return <StoreEmailLogin onReady={() => { setSession(null); setEmailEntryReady(true); }} />;
  }

  if (!sessionValid) {
    pushSiteAuthPath('access');
    return <StoreProfileGate onUnlock={(next) => setSession(next)} />;
  }

  return <StoreQueueWorkspace session={session} onSwitch={() => { clearStoreSession(); setSession(null); setEmailEntryReady(true); pushSiteAuthPath('access'); }} />;
}
